// src/context/BiodataCacheContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import { biodataAPI } from "utils/api";

const BiodataCacheContext = createContext(null);

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

export const BiodataCacheProvider = ({ children }) => {
  // Cache structure: { cacheKey: { data, timestamp, pagination } }
  const cacheRef = useRef(new Map());
  // Photo cache: { biodataId: url }
  const photoCacheRef = useRef(new Map());
  const photoQueueRef = useRef([]);
  const activePhotoFetchesRef = useRef(0);
  const pendingPhotoSetRef = useRef(new Set());
  const MAX_PHOTO_FETCH_CONCURRENCY = 2;
  const [cacheVersion, setCacheVersion] = useState(0); // Force re-render when cache updates

  /**
   * Generate cache key from status and filters
   */
  const generateCacheKey = (status, filters = {}, page = 1, perPage = 10) => {
    const filterStr = Object.keys(filters)
      .sort()
      .map((key) => `${key}:${filters[key] || ""}`)
      .join("|");
    return `${status}|${filterStr}|page:${page}|per_page:${perPage}`;
  };

  /**
   * Check if cache entry is still valid
   */
  const isCacheValid = (entry) => {
    if (!entry) return false;
    const now = Date.now();
    return now - entry.timestamp < CACHE_DURATION;
  };

  /**
   * Get biodata from cache or API
   */
  const getBiodataByStatus = useCallback(async (status, params = {}) => {
    const { page = 1, per_page = 10, ...filters } = params;
    const cacheKey = generateCacheKey(status, filters, page, per_page);

    // Check cache first
    const cached = cacheRef.current.get(cacheKey);
    if (isCacheValid(cached)) {
      return {
        status: "success",
        success: true,
        records: cached.data,
        data: { data: cached.data },
        pagination: cached.pagination,
        fromCache: true,
      };
    }

    // Cache miss - fetch from API
    try {
      const result = await biodataAPI.getBiodataByStatus(status, params);

      if (result.success) {
        const items = Array.isArray(result.records)
          ? result.records
          : Array.isArray(result.data?.data)
          ? result.data.data
          : [];

        // Cache photo URLs opportunistically from list items
        items.forEach((b) => {
          const url = extractPrimaryPhotoUrl(b);
          if (url) photoCacheRef.current.set(b.id, url);
        });

        // Store in cache
        cacheRef.current.set(cacheKey, {
          data: items,
          pagination: result.pagination,
          timestamp: Date.now(),
        });

        // Trigger re-render
        setCacheVersion((v) => v + 1);

        return {
          ...result,
          fromCache: false,
        };
      }

      return result;
    } catch (error) {
      console.error("Failed to fetch biodata:", error);
      throw error;
    }
  }, []);

  /**
   * Get all biodatas (for admin/master dashboard)
   */
  const getAllBiodatas = useCallback(async (params = {}) => {
    const { page = 1, per_page = 10, ...filters } = params;
    const cacheKey = generateCacheKey("all", filters, page, per_page);

    const cached = cacheRef.current.get(cacheKey);
    if (isCacheValid(cached)) {
      return {
        status: "success",
        success: true,
        records: cached.data,
        data: { data: cached.data },
        pagination: cached.pagination,
        fromCache: true,
      };
    }

    try {
      const result = await biodataAPI.getAllBiodata(params);

      if (result.status === "success" || result.success) {
        const items = result.records || result.data?.data || [];

        cacheRef.current.set(cacheKey, {
          data: items,
          pagination: result.pagination,
          timestamp: Date.now(),
        });

        setCacheVersion((v) => v + 1);

        return {
          success: true,
          records: items,
          data: { data: items },
          pagination: result.pagination,
          fromCache: false,
        };
      }

      return result;
    } catch (error) {
      console.error("Failed to fetch all biodatas:", error);
      throw error;
    }
  }, []);

  /**
   * Invalidate cache for specific status or all
   */
  const invalidateCache = useCallback((status = null) => {
    if (status) {
      // Invalidate all cache entries for specific status
      const keysToDelete = [];
      for (const [key] of cacheRef.current.entries()) {
        if (key.startsWith(`${status}|`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach((key) => cacheRef.current.delete(key));
    } else {
      // Clear all cache
      cacheRef.current.clear();
    }
    setCacheVersion((v) => v + 1);
  }, []);

  /**
   * Invalidate cache when biodata is updated (approve/reject/edit)
   */
  const invalidateOnUpdate = useCallback(
    (biodataIds, oldStatus, newStatus) => {

      // Invalidate old and new status caches
      if (oldStatus) invalidateCache(oldStatus);
      if (newStatus && newStatus !== oldStatus) invalidateCache(newStatus);

      // Also invalidate 'all' cache
      const keysToDelete = [];
      for (const [key] of cacheRef.current.entries()) {
        if (key.startsWith("all|")) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach((key) => cacheRef.current.delete(key));

      setCacheVersion((v) => v + 1);
    },
    [invalidateCache]
  );

  /**
   * Extract a primary photo URL from a biodata record without API calls
   */
  const extractPrimaryPhotoUrl = (b) => {
    if (!b) return "";
    const direct =
      b.photo_url || b.photoUrl || b.primary_photo_url || b.primaryPhotoUrl;
    if (direct) return direct;
    const photos = Array.isArray(b.photos)
      ? b.photos
      : Array.isArray(b.biodata_photos)
      ? b.biodata_photos
      : [];
    if (photos.length) {
      const primary =
        photos.find((p) => p.is_primary || p.primary) || photos[0];
      return primary?.file_url || primary?.url || "";
    }
    return "";
  };

  /**
   * Get cached photo URL for a biodata id; optionally seed from a provided record
   */
  const getPhotoUrl = useCallback((biodataId, record) => {
    if (!biodataId) return "";
    const cached = photoCacheRef.current.get(biodataId);
    if (cached) return cached;
    const derived = extractPrimaryPhotoUrl(record);
    if (derived) photoCacheRef.current.set(biodataId, derived);
    return derived || "";
  }, []);

  /**
   * Manually cache a photo URL
   */
  const cachePhotoUrl = useCallback((biodataId, url) => {
    if (!biodataId || !url) return;
    photoCacheRef.current.set(biodataId, url);
    setCacheVersion((v) => v + 1);
  }, []);

  const startPhotoWorker = useCallback(async () => {
    if (activePhotoFetchesRef.current >= MAX_PHOTO_FETCH_CONCURRENCY) return;
    const nextId = photoQueueRef.current.shift();
    if (!nextId) return;
    activePhotoFetchesRef.current += 1;
    try {
      const resp = await biodataAPI.getPhotos(nextId);
      const list = resp?.data || resp || [];
      let url = "";
      if (Array.isArray(list) && list.length) {
        const primary = list.find((p) => p.is_primary || p.primary) || list[0];
        url = primary?.file_url || primary?.url || "";
      }
      if (url) photoCacheRef.current.set(nextId, url);
    } catch (e) {
    } finally {
      pendingPhotoSetRef.current.delete(nextId);
      activePhotoFetchesRef.current -= 1;
      setCacheVersion((v) => v + 1);
      if (photoQueueRef.current.length) startPhotoWorker();
    }
  }, []);

  const prefetchPhotoUrl = useCallback(
    (biodataId) => {
      if (!biodataId) return;
      if (photoCacheRef.current.has(biodataId)) return;
      if (pendingPhotoSetRef.current.has(biodataId)) return;
      pendingPhotoSetRef.current.add(biodataId);
      photoQueueRef.current.push(biodataId);
      startPhotoWorker();
    },
    [startPhotoWorker]
  );

  const prefetchPhotoUrls = useCallback(
    (ids = []) => {
      ids.forEach((id) => prefetchPhotoUrl(id));
    },
    [prefetchPhotoUrl]
  );

  const shouldPrefetchPhoto = (record) => {
    if (!record || !record.id) return false;
    const direct =
      record.photo_url ||
      record.photoUrl ||
      record.primary_photo_url ||
      record.primaryPhotoUrl;
    if (direct) return false;
    if (photoCacheRef.current.has(record.id)) return false;
    if (pendingPhotoSetRef.current.has(record.id)) return false;
    return true;
  };

  const prefetchPhotoUrlsLimited = useCallback(
    (records = [], limit = 8) => {
      let count = 0;
      for (let i = 0; i < records.length && count < limit; i++) {
        const rec = records[i];
        if (shouldPrefetchPhoto(rec)) {
          prefetchPhotoUrl(rec.id);
          count++;
        }
      }
    },
    [prefetchPhotoUrl]
  );

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(() => {
    const stats = {
      totalEntries: cacheRef.current.size,
      validEntries: 0,
      expiredEntries: 0,
      byStatus: {},
    };

    for (const [key, entry] of cacheRef.current.entries()) {
      const status = key.split("|")[0];
      if (!stats.byStatus[status]) {
        stats.byStatus[status] = { valid: 0, expired: 0 };
      }

      if (isCacheValid(entry)) {
        stats.validEntries++;
        stats.byStatus[status].valid++;
      } else {
        stats.expiredEntries++;
        stats.byStatus[status].expired++;
      }
    }

    return stats;
  }, [cacheVersion]); // Include cacheVersion to trigger recalc

  /**
   * Prefetch data for common pages (optional optimization)
   */
  const prefetchCommonPages = useCallback(async () => {
    const statuses = ["pending", "approved", "rejected", "resubmitted"];

    // Prefetch first page of each status (fire and forget)
    statuses.forEach((status) => {
      getBiodataByStatus(status, { page: 1, per_page: 10 }).catch((err) => {
        console.warn(`Prefetch failed for ${status}:`, err);
      });
    });
  }, [getBiodataByStatus]);

  const value = {
    getBiodataByStatus,
    getAllBiodatas,
    invalidateCache,
    invalidateOnUpdate,
    getCacheStats,
    prefetchCommonPages,
    prefetchPhotoUrl,
    prefetchPhotoUrls,
    prefetchPhotoUrlsLimited,
    getPhotoUrl,
    cachePhotoUrl,
    cacheVersion, // Expose for debugging
  };

  return (
    <BiodataCacheContext.Provider value={value}>
      {children}
    </BiodataCacheContext.Provider>
  );
};

/**
 * Hook to use biodata cache
 */
export const useBiodataCache = () => {
  const context = useContext(BiodataCacheContext);
  if (!context) {
    throw new Error("useBiodataCache must be used within BiodataCacheProvider");
  }
  return context;
};

export default BiodataCacheContext;
